// ============================================================================
// SkipModal — confirm skipping a stop for today, with an optional reason.
// Skipping only touches the day layer (am_route_day_stops); the template and
// tomorrow's rotation are untouched.
// ============================================================================

import { useState } from 'react';
import { overlayClickClose } from '../../../shared/overlay-close';
import { errorMessage } from '../../../shared/errors';

export interface SkipModalProps {
  stopLabel: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
}

export default function SkipModal({ stopLabel, onClose, onConfirm }: SkipModalProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setSubmitting(true);
    setErr(null);
    try {
      await onConfirm(reason);
    } catch (e) {
      setErr(errorMessage(e, 'Could not skip stop.'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-4"
      {...overlayClickClose(onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl border border-tcs-border bg-tcs-overlay p-5 shadow-2xl sm:rounded-xl"
      >
        <h3 className="text-base font-semibold text-tcs-text">
          Skip this stop today?
        </h3>
        <p className="mt-0.5 text-sm text-tcs-text-secondary">{stopLabel}</p>
        <div className="mt-3 rounded-md border border-tcs-border bg-tcs-bg-muted p-3 text-xs text-tcs-text-secondary">
          This only affects <strong>today</strong>. Your normal rotation is
          unchanged — the stop returns tomorrow.
        </div>
        <label className="mt-4 block">
          <span className="text-xs font-medium text-tcs-text-secondary">
            Reason (optional)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. POC out, covered by another AM, emergency elsewhere"
            className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-field p-2 text-base shadow-inner focus:border-tcs-accent focus:outline-none"
          />
        </label>
        {err && <p className="mt-2 text-xs text-rag-red">{err}</p>}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-10 rounded-lg border border-tcs-border bg-tcs-raised px-4 text-sm font-medium text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={go}
            disabled={submitting}
            className="h-10 rounded-lg bg-tcs-inverse px-4 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
          >
            {submitting ? 'Skipping…' : 'Skip today'}
          </button>
        </div>
      </div>
    </div>
  );
}
